/**
 * Lip crop extractor — cut a wide smile ROI from the source image for tooth AI.
 */

import {
  SMILE_CROP_PADDING_X,
  SMILE_CROP_PADDING_Y,
  TOOTH_DETECT_MAX_SCALE,
  TOOTH_DETECT_MIN_WIDTH,
} from "./constants";
import { remapPointsToLocal } from "./landmarkProcessing";
import {
  boundingBoxFromPoints,
  clampBoundingBox,
} from "./smileRegionExtraction";
import type {
  DentalGuides,
  LipCropResult,
  Point2D,
  SmileRegion,
  ToothDetectionResult,
  ToothOutline,
} from "./types";

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image for lip crop"));
    img.src = src;
  });
}

/** Expand a polygon outward from its centroid (for a looser tooth-detection mask). */
export function expandPolygon(points: Point2D[], factor: number): Point2D[] {
  if (points.length < 3) return points.map((p) => ({ ...p }));
  const cx = points.reduce((s, p) => s + p.x, 0) / points.length;
  const cy = points.reduce((s, p) => s + p.y, 0) / points.length;
  return points.map((p) => ({
    x: cx + (p.x - cx) * factor,
    y: cy + (p.y - cy) * factor,
  }));
}

/**
 * Extract a larger smile region for higher tooth-detection accuracy.
 * Uses wider horizontal padding so lateral teeth stay in frame.
 */
export async function extractLipCrop(
  imageSrc: string,
  smileRegion: SmileRegion,
  padding:
    | number
    | { x?: number; y?: number } = {
      x: SMILE_CROP_PADDING_X,
      y: SMILE_CROP_PADDING_Y,
    }
): Promise<LipCropResult> {
  const img = await loadImage(imageSrc);
  const pad =
    typeof padding === "number"
      ? padding
      : {
          x: padding.x ?? SMILE_CROP_PADDING_X,
          y: padding.y ?? SMILE_CROP_PADDING_Y,
        };

  // Prefer outer lip + hull points so the crop covers full smile width
  const cropPoints = [
    ...smileRegion.mouthLandmarks,
    ...smileRegion.outerPolygon,
    ...smileRegion.convexHull,
  ];

  const box = clampBoundingBox(
    boundingBoxFromPoints(cropPoints, pad),
    img.naturalWidth || img.width,
    img.naturalHeight || img.height
  );

  const canvas = document.createElement("canvas");
  canvas.width = box.width;
  canvas.height = box.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not create 2D context for lip crop");

  ctx.drawImage(
    img,
    box.x,
    box.y,
    box.width,
    box.height,
    0,
    0,
    box.width,
    box.height
  );

  return {
    dataUrl: canvas.toDataURL("image/png"),
    width: box.width,
    height: box.height,
    sourceBox: box,
    innerPolygonLocal: remapPointsToLocal(
      smileRegion.innerPolygon,
      box.x,
      box.y
    ),
    outerPolygonLocal: remapPointsToLocal(
      smileRegion.outerPolygon,
      box.x,
      box.y
    ),
  };
}

export async function lipCropToCanvasAsync(
  crop: LipCropResult
): Promise<HTMLCanvasElement> {
  const img = await loadImage(crop.dataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not create canvas from lip crop");
  ctx.drawImage(img, 0, 0);
  return canvas;
}

/**
 * Upscale lip crop for tooth detection, then scale contours back to crop space.
 * Mouth mask is the inner lip lining (or an edited override) so teeth stay inside.
 */
export async function prepareToothDetectionCanvas(
  crop: LipCropResult,
  options?: { mouthMaskOverride?: Point2D[] }
): Promise<{
  canvas: HTMLCanvasElement;
  scale: number;
  mouthMask: Point2D[];
}> {
  const base = await lipCropToCanvasAsync(crop);
  const scale = Math.min(
    TOOTH_DETECT_MAX_SCALE,
    Math.max(1, TOOTH_DETECT_MIN_WIDTH / Math.max(1, crop.width))
  );

  let canvas = base;
  if (scale > 1.01) {
    const up = document.createElement("canvas");
    up.width = Math.round(crop.width * scale);
    up.height = Math.round(crop.height * scale);
    const ctx = up.getContext("2d");
    if (!ctx) throw new Error("Could not create upscaled tooth canvas");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(base, 0, 0, up.width, up.height);
    canvas = up;
  }

  // Prefer user-edited inner lip contour, else detected lining
  const baseMask =
    options?.mouthMaskOverride && options.mouthMaskOverride.length >= 3
      ? options.mouthMaskOverride
      : crop.innerPolygonLocal.length >= 3
        ? crop.innerPolygonLocal
        : crop.outerPolygonLocal;
  // ~6% pad so enamel at the lip edge / commissures isn't clipped
  const expanded = expandPolygon(baseMask, 1.06);
  const mouthMask = expanded.map((p) => ({
    x: p.x * scale,
    y: p.y * scale,
  }));

  return { canvas, scale, mouthMask };
}

function pointInPolygon(point: Point2D, polygon: Point2D[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi + 1e-9) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function polygonCentroid(points: Point2D[]): Point2D {
  const x = points.reduce((s, p) => s + p.x, 0) / Math.max(1, points.length);
  const y = points.reduce((s, p) => s + p.y, 0) / Math.max(1, points.length);
  return { x, y };
}

/**
 * Pull any contour point outside the inner lip back toward the polygon centroid
 * so tooth outlines cannot spill past the lip lining.
 * Edge teeth whose centroid sits just outside the lip are rescued instead of dropped.
 */
export function clipTeethToInnerLip(
  teeth: ToothOutline[],
  innerLip: Point2D[]
): ToothOutline[] {
  if (innerLip.length < 3 || !teeth.length) return teeth;

  const mask = expandPolygon(innerLip, 1.06);
  const lipCenter = polygonCentroid(mask);

  return teeth
    .map((tooth, index) => {
      let center = polygonCentroid(tooth.contour);
      if (!pointInPolygon(center, mask)) {
        // Pull center toward lip interior — keep corner teeth near commissures
        let cx = center.x;
        let cy = center.y;
        let rescued = false;
        for (let step = 0; step < 18; step++) {
          cx = cx * 0.72 + lipCenter.x * 0.28;
          cy = cy * 0.72 + lipCenter.y * 0.28;
          if (pointInPolygon({ x: cx, y: cy }, mask)) {
            center = { x: cx, y: cy };
            rescued = true;
            break;
          }
        }
        if (!rescued) return null;
      }

      const contour = tooth.contour.map((p) => {
        if (pointInPolygon(p, mask)) return { ...p };
        // Project toward tooth center until inside (or fallback to center)
        let x = p.x;
        let y = p.y;
        for (let step = 0; step < 12; step++) {
          x = x * 0.65 + center.x * 0.35;
          y = y * 0.65 + center.y * 0.35;
          if (pointInPolygon({ x, y }, mask)) return { x, y };
        }
        return { x: center.x, y: center.y };
      });

      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const p of contour) {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
      }

      return {
        ...tooth,
        id: `tooth-${index}`,
        index,
        contour,
        boundingBox: {
          x: minX,
          y: minY,
          width: Math.max(1, maxX - minX),
          height: Math.max(1, maxY - minY),
        },
      };
    })
    .filter((t): t is ToothOutline => t !== null)
    .map((t, index) => ({ ...t, id: `tooth-${index}`, index }));
}

export function scaleToothResultToCrop(
  result: ToothDetectionResult,
  scale: number
): ToothDetectionResult {
  if (scale <= 1.01) return result;
  const inv = 1 / scale;

  const scaleBox = (b: ToothOutline["boundingBox"]) => ({
    x: b.x * inv,
    y: b.y * inv,
    width: b.width * inv,
    height: b.height * inv,
  });

  const teeth: ToothOutline[] = result.teeth.map((t, index) => ({
    ...t,
    id: `tooth-${index}`,
    index,
    contour: t.contour.map((p) => ({ x: p.x * inv, y: p.y * inv })),
    boundingBox: scaleBox(t.boundingBox),
    area: t.area * inv * inv,
  }));

  let guides: DentalGuides | undefined;
  if (result.guides) {
    guides = {
      midlineX: result.guides.midlineX * inv,
      occlusalY: result.guides.occlusalY * inv,
      smileArc: result.guides.smileArc.map((p) => ({
        x: p.x * inv,
        y: p.y * inv,
      })),
      gingivalArc: (result.guides.gingivalArc ?? []).map((p) => ({
        x: p.x * inv,
        y: p.y * inv,
      })),
      verticalDividers: (result.guides.verticalDividers ?? []).map(
        (x) => x * inv
      ),
      toothCenterLines: (result.guides.toothCenterLines ?? []).map(
        (x) => x * inv
      ),
    };
  }

  return { ...result, teeth, guides };
}
